// Small building blocks shared by the Target / Mapping / Automation / Test & publish steps.
import { ReactNode } from 'react';
import { Autocomplete, Box, Paper, Stack, TextField, Typography } from '@mui/material';

import type { ApiEnvelope, CatalogueField, IntegrationProcess, PipelineSpec, UpdatePolicy } from '../../types';

export const MONO = '"JetBrains Mono", "SFMono-Regular", Menlo, Consolas, monospace';

/** Error text from a rejected axios call (the interceptor rejects with the response body) or an Error. */
export const errMsg = (e: any, fallback = 'Something went wrong'): string => {
  if (typeof e === 'string' && e) return e;
  const m = e?.message;
  if (Array.isArray(m)) return m.join(', ');
  return typeof m === 'string' && m ? m : fallback;
};

/** diApi resolves the envelope; a `success: false` body is still an error. */
export const unwrap = <T,>(res: ApiEnvelope<T>): T => {
  if (res && res.success === false) throw new Error(res.message || 'Something went wrong');
  return res.data;
};

export const newUid = (): string => {
  const c: any = typeof window !== 'undefined' ? (window as any).crypto : null;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    return (ch === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
};

export const formatDateTime = (iso?: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};

export const UPDATE_POLICY_LABEL: Record<UpdatePolicy, string> = {
  SOURCE_WINS: "Always use Aljex's value",
  FILL_EMPTY: 'Fill only if empty',
  CREATE_ONLY: 'Never update'
};

/** The process whose first step the mapping fills: the single target, or (MVP) the first routed process. */
export const mappedProcess = (spec: PipelineSpec, processes: IntegrationProcess[]): IntegrationProcess | null => {
  const t = spec.target;
  if (t.mode === 'SINGLE') return processes.find((p) => p.uid === t.teamUid) || null;
  const firstRouted = t.routes.find((r) => !!r.teamUid);
  return firstRouted ? processes.find((p) => p.uid === firstRouted.teamUid) || null : null;
};

/** A field name in the source, shown in mono. */
export const SourceName = ({ children }: { children: ReactNode }) => (
  <Box
    component="code"
    sx={{ fontFamily: MONO, fontSize: '0.8rem', px: 0.75, py: 0.25, borderRadius: 1, bgcolor: 'action.hover', whiteSpace: 'nowrap' }}
  >
    {children}
  </Box>
);

export const PanelHead = ({ title, subtitle }: { title: string; subtitle?: ReactNode }) => (
  <Stack spacing={0.5}>
    <Typography variant="h4">{title}</Typography>
    {subtitle && (
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '72ch' }}>
        {subtitle}
      </Typography>
    )}
  </Stack>
);

export const SubCard = ({ title, action, children }: { title?: ReactNode; action?: ReactNode; children: ReactNode }) => (
  <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
    {(title || action) && (
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        {typeof title === 'string' ? <Typography variant="h5">{title}</Typography> : title}
        {action}
      </Stack>
    )}
    {children}
  </Paper>
);

/** "Aljex has it on 82%" mini bar. */
export const FillBar = ({ pct }: { pct: number }) => {
  const v = Math.max(0, Math.min(100, Math.round(pct)));
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Box sx={{ width: 64, height: 6, borderRadius: 3, bgcolor: 'divider', overflow: 'hidden' }}>
        <Box sx={{ width: `${v}%`, height: '100%', bgcolor: v >= 80 ? 'success.main' : v >= 40 ? 'warning.main' : 'error.main' }} />
      </Box>
      <Typography variant="caption" color="text.secondary">
        Aljex has it on {v}%
      </Typography>
    </Stack>
  );
};

/** Pick a source field from the catalogue (free text allowed — the catalogue is sampled). */
export const CatalogueFieldPicker = ({
  catalogue,
  value,
  onChange,
  label,
  placeholder = 'Pick an Aljex field',
  width = 260,
  size = 'small',
  commitOnType = true
}: {
  catalogue: CatalogueField[];
  value: string;
  onChange: (field: string) => void;
  label?: string;
  placeholder?: string;
  width?: number | string;
  size?: 'small' | 'medium';
  /** false: only commit on pick / Enter (for pickers that unmount once a value is set). */
  commitOnType?: boolean;
}) => (
  <Autocomplete
    freeSolo
    size={size}
    options={catalogue.map((c) => c.field)}
    value={value || null}
    onChange={(_, v) => onChange((v as string | null) || '')}
    onInputChange={(_, v, reason) => {
      if (commitOnType && reason === 'input') onChange(v);
    }}
    renderOption={(props, option) => {
      const c = catalogue.find((x) => x.field === option);
      return (
        <li {...props} key={option}>
          <Stack sx={{ width: '100%' }} direction="row" justifyContent="space-between" spacing={2}>
            <Box component="span" sx={{ fontFamily: MONO, fontSize: '0.8rem' }}>
              {option}
            </Box>
            {c && (
              <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 160 }}>
                {c.fillPct}%{c.samples[0] ? ` · ${c.samples[0]}` : ''}
              </Typography>
            )}
          </Stack>
        </li>
      );
    }}
    sx={{ width }}
    renderInput={(params) => (
      <TextField
        {...params}
        label={label}
        placeholder={placeholder}
        inputProps={{ ...params.inputProps, style: { fontFamily: MONO, fontSize: '0.85rem' } }}
      />
    )}
  />
);
